<template>
  <div>
    <h1>Recorded games</h1>
    <router-link :to="{ name: 'TrackGame' }">Track a game</router-link>

    <h2>Win percentage</h2>
    <p>
      Hover for details. Note that mirror matches are not counted in win rates.
    </p>
    <b-table
      :items="filteredItems"
      :fields="winRatioFields"
      responsive
      small
      class="compact-table-sm vertical-sort"
    >
      <template v-slot:thead-top>
        <b-tr>
          <b-th><span class="sr-only">Deck Name</span></b-th>
          <b-th :colspan="winRatioFields.length - 1" class="text-center"
            >Defeated</b-th
          >
        </b-tr>
      </template>

      <template #head()="data">
        <div>{{ data.label }}</div>
      </template>

      <template #cell(name)="data">
        <router-link
          :to="{ name: 'UmDbDeck', params: { slug: data.item.slug } }"
        >
          {{ data.value }}
        </router-link>
      </template>

      <template #cell()="data">
        <span
          :id="$id(data.item.id + '__' + data.field.key)"
          :class="[
            data.field.key != 'averageWinRate' &&
            data.item[data.field.key].total_games
              ? 'info-tooltip'
              : '',
          ]"
          tabindex="0"
          >{{ data.value }}</span
        >
        <b-popover
          v-if="
            data.field.key != 'averageWinRate' &&
            data.item[data.field.key].total_games
          "
          :key="'popover-' + data.item.id + '__' + data.field.key"
          :target="$id(data.item.id + '__' + data.field.key)"
          triggers="hover focus"
          placement="auto"
          :title="data.item.name + ' vs. ' + data.field.label"
        >
          <span v-if="data.item[data.field.key].victories !== null">
            Won {{ data.item[data.field.key].victories }} out of
            {{ data.item[data.field.key].total_games }} games
          </span>
          <span v-else>
            Played {{ data.item[data.field.key].total_games }} games
          </span>
        </b-popover>
      </template>
    </b-table>

    <b-form-group
      :label="`Minimum number of games to include in average: ${countedGames}`"
      label-class="font-weight-bold"
    >
      <b-form-row class="form-group">
        <b-col cols="auto" class="text-right">
          <label>
            {{ minCountedGames }}
          </label>
        </b-col>
        <b-col>
          <b-form-input
            type="range"
            :min="minCountedGames"
            :max="maxCountedGames"
            v-model="countedGames"
            number
          />
        </b-col>
        <b-col cols="auto">
          <label>
            {{ maxCountedGames }}
          </label>
        </b-col>
      </b-form-row>
    </b-form-group>

    <h2>Games played</h2>
    <p>Note that mirror matches are shown, but not included in total.</p>
    <b-table
      :items="items"
      :fields="totalGamesFields"
      responsive
      small
      class="compact-table-sm vertical-sort"
    >
      <template #head()="data">
        <div>{{ data.label }}</div>
      </template>

      <template #cell(name)="data">
        <router-link
          :to="{ name: 'UmDbDeck', params: { slug: data.item.slug } }"
        >
          {{ data.value }}
        </router-link>
      </template>
    </b-table>
  </div>
</template>

<script>
import api from "@/api";

export default {
  name: "ResultsGrid",
  metaInfo: {
    title: "All Results",
  },
  data() {
    return {
      items: [],
      winRatioFields: [],
      totalGamesFields: [],
      maxTotalGames: 0,
      labels: [],
      minCountedGames: 1,
      maxCountedGames: process.env.VUE_APP_RESULTSGRID_MAXCOUNTEDGAMES,
    };
  },
  computed: {
    countedGames: {
      get() {
        if ("ResultsGridCountedGames" in this.$store.state.userPreferences) {
          return this.$store.state.userPreferences["ResultsGridCountedGames"];
        }
        return 1;
      },
      set(value) {
        this.$store.commit("setPreference", {
          key: "ResultsGridCountedGames",
          value: value,
        });
      },
    },
    filteredItems() {
      return this.items.map((fighter) => {
        const initialVals = { avg: 0, n: 0 };
        const averageWinRate = this.labels
          .map((label) => fighter[label.key])
          .reduce(this.getAverageWinRate, initialVals);
        const { avg, n } = averageWinRate;
        return { ...fighter, averageWinRate: n ? avg : null };
      });
    },
  },
  methods: {
    formatWinRatio(value) {
      if (value.victories !== null && value.total_games > 0) {
        return (value.victories / value.total_games).toLocaleString("en", {
          style: "percent",
        });
      }
      return "-";
    },
    winRatioClass(value) {
      const winRatioClasses = [];
      if (value.victories !== null) {
        if (value.total_games < this.countedGames) {
          winRatioClasses.push("resultgrid-filtered");
        }
        const winRatio = value.victories / value.total_games;
        winRatioClasses.push(`resultgrid-bg${Math.round(winRatio * 10)}`);
      }
      return winRatioClasses;
    },
    formatAverage(value) {
      if (value !== null) {
        return value.toLocaleString("en", {
          style: "percent",
        });
      }
      return "-";
    },
    averageClass(value) {
      if (!isNaN(parseFloat(value))) {
        // if (value !== null) {
        return `resultgrid-bg${Math.round(value * 10)}`;
      }
      return "resultgrid-filtered";
    },
    totalGamesClass(val) {
      return this.logClass(val, this.maxTotalGames);
    },
    totalTotalClass(val) {
      return this.logClass(val, this.maxTotalTotal);
    },
    logClass(val, max) {
      if (typeof val !== "number" || val === 0) return null;
      const logVal = Math.log(val);
      const logMax = Math.log(max);
      return `resultgrid-bg${Math.round((logVal / logMax) * 10)}`;
    },
    getAverageWinRate({ avg, n }, fighterInfo) {
      if (
        fighterInfo.victories !== null &&
        fighterInfo.total_games >= this.countedGames
      ) {
        const winRate = fighterInfo.victories / fighterInfo.total_games;
        return {
          avg: (winRate + n * avg) / (n + 1),
          n: n + 1,
        };
      }
      // skip to the next one
      return { avg, n };
    },
  },
  mounted() {
    api.getResultsGrid().then((data) => {
      this.items = data.grid;
      this.maxTotalGames = data.max_total_games;
      this.maxTotalTotal = data.max_total_total;
      this.labels = data.labels;
      const winRatioFields = [
        {
          key: "name",
          label: "Winner",
          sortable: true,
          stickyColumn: true,
          class: ["truncate-col"],
        },
      ];
      const totalGamesFields = [
        {
          key: "name",
          label: "Deck",
          sortable: true,
          stickyColumn: true,
          class: ["truncate-col"],
        },
      ];

      this.labels.forEach((label) => {
        winRatioFields.push({
          key: `${label.key}`,
          label: label.label,
          formatter: "formatWinRatio",
          sortByFormatted: true,
          thClass: "resultgrid-vertical",
          tdClass: this.winRatioClass,
          class: "text-center",
          slug: label.slug,
          sortable: true,
          sortDirection: "desc",
        });
        totalGamesFields.push({
          key: `${label.key}.total_games`,
          label: label.label,
          thClass: "resultgrid-vertical",
          tdClass: this.totalGamesClass,
          class: "text-center",
          slug: label.slug,
          sortable: true,
          sortDirection: "desc",
        });
      });

      winRatioFields.push({
        key: "total",
        formatter: "formatWinRatio",
        sortByFormatted: true,
        thClass: "resultgrid-vertical",
        tdClass: this.winRatioClass,
        class: "text-center font-weight-bold",
        sortable: true,
        sortDirection: "desc",
      });
      winRatioFields.push({
        key: "averageWinRate",
        label: "Average",
        formatter: "formatAverage",
        thClass: "resultgrid-vertical",
        tdClass: this.averageClass,
        class: "text-center font-weight-bold",
        sortable: true,
        sortDirection: "desc",
      });
      totalGamesFields.push({
        key: "total.total_games",
        label: "Total",
        thClass: "resultgrid-vertical",
        tdClass: this.totalTotalClass,
        class: "text-center font-weight-bold",
        sortable: true,
        sortDirection: "desc",
      });

      this.winRatioFields = winRatioFields;
      this.totalGamesFields = totalGamesFields;
    });
  },
};
</script>

<style>
thead tr th.resultgrid-vertical div {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  margin: 0 auto;
  white-space: nowrap;
}
/*
    Colourblindness friendly palette from
    https://gka.github.io/palettes/#/11|d|005a74,3aa794,e7e3bc|e7e3bc,eda34e,db4325|1|1

    Text contrast checked here: https://webaim.org/resources/contrastchecker/;
*/

.resultgrid-filtered {
  opacity: 0.33;
}

.resultgrid-bg10 {
  background-color: #005a74;
  color: #fff;
}
.resultgrid-bg9 {
  background-color: #2a7781;
  color: #fff;
}
.resultgrid-bg8 {
  background-color: #51948f;
  color: #000;
}
.resultgrid-bg7 {
  background-color: #7db09d;
  color: #000;
}
.resultgrid-bg6 {
  background-color: #afcaac;
  color: #000;
}
.resultgrid-bg5 {
  background-color: #e7e7bc;
  color: #000;
}
.resultgrid-bg4 {
  background-color: #edc68e;
  color: #000;
}
.resultgrid-bg3 {
  background-color: #eda869;
  color: #000;
}
.resultgrid-bg2 {
  background-color: #e9894c;
  color: #000;
}
.resultgrid-bg1 {
  background-color: #e36935;
  color: #000;
}
.resultgrid-bg0 {
  background-color: #db4325;
  color: #000;
}
</style>
